import type { PlanListCounts } from '@developerz/domain';
import { useSearchParams } from '@solidjs/router';
import { createEffect, createMemo, type JSX, on, Show, untrack } from 'solid-js';
import { t, tPlural } from '../i18n';
import {
  CUSTOM_PLANS_DEFAULT_PAGE,
  CUSTOM_PLANS_PAGE_PARAM,
  CUSTOM_PLANS_TAB_ID_PREFIX,
  type CustomPlanParams,
  EMPTY_CUSTOM_PLAN_COUNTS,
  type PlanBucketOrAll,
  parseCustomPlanParams,
} from '../lib/custom-plans';
import { useDebouncedSearch } from '../lib/debounced-search';
import { type PlanListParams, useCustomPlans } from '../lib/plan';
import { SESSION_TOKEN } from '../lib/session';
import { CustomPlansFilters } from './CustomPlansFilters';
import { CustomPlansPanels } from './CustomPlansPanels';
import { type TabItem, TabList } from './TabList';

// The "Custom plans" section (#4341, #5136): the plans a PERSON started
// (`initiated_by=user`, #4340 / #4727), sitting ABOVE the "all plans" panel
// (`routes/Plans.tsx`) from its OWN query.
//
// AFTER #5136 every filter is server-side: status / repo / q go on the
// request; the response carries `counts` so the tab rail has its numbers
// without a second read; pagination is numbered with `?custom_page=`. The
// URL is the source of truth for ALL of them (`parseCustomPlanParams`), so
// a back/forward step lands on the same view a human read. SIX tab panels,
// ONE visible, named via `tabPanelId(...)` so every tab's `aria-controls`
// resolves whether or not it is currently visible (`CustomPlansPanels.tsx`
// owns that scaffold and the per-tab loading/error/empty split).

type TabDescriptor = { readonly id: PlanBucketOrAll; readonly labelKey: string };

const TAB_DESCRIPTORS: readonly TabDescriptor[] = [
  { id: 'all', labelKey: 'fleet.plans.custom.tab_all' },
  { id: 'awaiting_approval', labelKey: 'fleet.plans.custom.tab_awaiting_approval' },
  { id: 'in_progress', labelKey: 'fleet.plans.custom.tab_in_progress' },
  { id: 'stalled', labelKey: 'fleet.plans.custom.tab_stalled' },
  { id: 'completed', labelKey: 'fleet.plans.custom.tab_completed' },
  { id: 'closed', labelKey: 'fleet.plans.custom.tab_closed' },
];

/**
 * The six tab items, built ONCE (same shape as `DonateStepRail.tsx`): object
 * identity stays stable for the life of the component, so `<For>` (keyed on
 * identity in both `TabList` and `CustomPlansPanels`) never destroys and
 * rebuilds a tab mid-interaction — rebuilding this array on every
 * `plans.data` change (a tab switch, a page, a filter, a poll) would drop
 * focus to `<body>` instead. Only `trail()` re-reads `counts`/`loaded`
 * reactively, at the one place it is actually invoked (inside each button's
 * JSX). Counts stay hidden until the first response lands — the same rule
 * the header's total already follows — so a zero before data arrives never
 * reads the same as a real zero.
 */
function buildTabItems(counts: () => PlanListCounts, loaded: () => boolean): readonly TabItem[] {
  return TAB_DESCRIPTORS.map((d) => ({
    id: d.id,
    label: t(d.labelKey),
    trail: (): JSX.Element => (
      <Show when={loaded()}>
        <span class="tabs__count badge">{String(counts()[d.id])}</span>
      </Show>
    ),
  }));
}

/**
 * The request params from the parsed URL state. Conditional spreads, never a
 * key set to `undefined`: `PlanListParams`' optional fields are real
 * TypeScript optionals under `exactOptionalPropertyTypes`, which treats
 * `{ bucket: undefined }` as a DIFFERENT (and disallowed) shape from omitting
 * `bucket` altogether. Each branch narrows `p`'s field itself (no `as`) —
 * `p.status !== 'all'` already leaves `PlanBucket`, `p.repo !== null` already
 * leaves `string`.
 */
function listParams(p: CustomPlanParams): PlanListParams {
  return {
    page: p.page,
    ...(p.status === 'all' ? {} : { bucket: p.status }),
    ...(p.repo !== null ? { repo: p.repo } : {}),
    ...(p.q !== null ? { q: p.q } : {}),
  };
}

export function CustomPlansSection(props: { token?: string | null } = {}): JSX.Element {
  // `=== undefined`, not `??`: an injected `null` MEANS "no session" (mirrors
  // `routes/Plans.tsx`).
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const [params, setParams] = useSearchParams();

  // URL is the source of truth. Re-parsed reactively so a back/forward step
  // OR a programmatic setParams re-derives the section without manual sync.
  const parsed = createMemo((): CustomPlanParams => parseCustomPlanParams(params));

  // The section owns the search `<input>`'s ref (not `CustomPlansPanels`,
  // which never renders it) so its own `/` shortcut, fired from the row
  // list, can focus it without a second document listener — declaring this
  // ref anywhere the input does not actually live would leave `/` silently
  // doing nothing.
  let searchInputRef: HTMLInputElement | undefined;
  const setSearchInputRef = (el: HTMLInputElement): void => {
    searchInputRef = el;
  };
  const getSearchInputRef = (): HTMLInputElement | undefined => searchInputRef;

  const { qInput, setQInput, debounce } = useDebouncedSearch();

  // Mirror the URL `q` into the local buffer when it changes from OUTSIDE the
  // input itself (back/forward, programmatic clear). A SEPARATE `urlQ` memo,
  // not `on(() => parsed().q, ...)` directly: `parsed` is itself a memo that
  // returns a FRESH object — status/repo/q/page all live on it — so `on()`
  // tracking `() => parsed().q` would subscribe to the WHOLE `parsed` memo
  // and re-run on ANY of those fields changing, not just `q` — a tab or repo
  // change mid-debounce would reset a not-yet-committed search. `urlQ`'s own
  // output is a plain string, so its `on()` only fires when that STRING
  // actually differs. Reading `qInput()` directly (not `untrack`) here would
  // also re-run this on every local keystroke, since `onQuery` calls
  // `setQInput` before the debounce ever commits: the effect would then see
  // its own just-typed value against the still-stale URL and reset the box
  // to the URL's OLD value immediately, so only the last character typed
  // would ever reach `?q=`. Compared TRIMMED against trimmed — the debounce
  // commits `next.trim()`, so a buffer holding `'audit '` (mid-word, not yet
  // committed) must read as the SAME search as a URL holding `'audit'`, or a
  // reader who pauses after a space gets the box reset out from under them
  // and ends up typing "auditlog".
  const urlQ = createMemo((): string | null => parsed().q);
  createEffect(
    on(urlQ, (raw) => {
      const v = raw ?? '';
      if (v !== untrack(qInput).trim()) setQInput(v);
    }),
  );

  const plans = useCustomPlans(() => listParams(parsed()), token);

  // Past-the-end guard, same shape as `routes/Plans.tsx`'s: the response's
  // `total` (this tab, after any filter) can be real and nonzero while
  // `plans` is empty — a stale `?custom_page=` bookmark, or a filter change
  // landing past the new (shorter) last page. That is NOT "this tab has
  // none": `CustomPlansPanels`' `isEmpty` gates on `data.page <= 1`, so a
  // past-the-end page (always > 1 to reach this branch) never trips its
  // account-empty/no-matches slot — but until THIS effect's own refetch
  // lands, `PanelBody` would otherwise paint its own "no matches" fallback
  // for the stale `plans: []`, which is why `CustomPlansPanels` renders a
  // loading skeleton instead for exactly that window. Reset to page 1 with
  // replace nav so a stale bookmark self-heals.
  createEffect(() => {
    const d = plans.data;
    if (d && d.total > 0 && d.plans.length === 0 && parsed().page > 1) {
      setParams({ [CUSTOM_PLANS_PAGE_PARAM]: undefined }, { replace: true });
    }
  });

  const counts = (): PlanListCounts => plans.data?.counts ?? EMPTY_CUSTOM_PLAN_COUNTS;
  const loaded = (): boolean => plans.data !== undefined;
  const items: readonly TabItem[] = buildTabItems(counts, loaded);
  const repoOptions = (): readonly string[] => plans.data?.repos ?? [];

  const planParam = (): string | null =>
    typeof params['plan'] === 'string' ? params['plan'] : null;
  const setPlanParam = (next: string | null): void => {
    setParams({ plan: next ?? undefined });
  };

  const setStatus = (next: string): void => {
    setParams({ status: next === 'all' ? undefined : next, [CUSTOM_PLANS_PAGE_PARAM]: undefined });
  };
  const setRepo = (next: string | null): void => {
    setParams({ repo: next ?? undefined, [CUSTOM_PLANS_PAGE_PARAM]: undefined });
  };
  const setPage = (page: number): void => {
    setParams({
      [CUSTOM_PLANS_PAGE_PARAM]: page === CUSTOM_PLANS_DEFAULT_PAGE ? undefined : String(page),
    });
  };
  const onQuery = (next: string): void => {
    setQInput(next);
    debounce(next, (value) => {
      setParams(
        { q: value === '' ? undefined : value, [CUSTOM_PLANS_PAGE_PARAM]: undefined },
        { replace: true },
      );
    });
  };

  return (
    <section class="custom-plans">
      <div class="custom-plans__header">
        <div>
          <h2>{t('fleet.plans.custom.title')}</h2>
          <p>{t('fleet.plans.custom.subtitle')}</p>
          <Show when={plans.data !== undefined}>
            <p>
              {tPlural('fleet.plans.custom.count', counts().all, { count: String(counts().all) })}
            </p>
          </Show>
        </div>
        <CustomPlansFilters
          repo={parsed().repo}
          q={qInput()}
          repoOptions={repoOptions()}
          onRepo={setRepo}
          onQuery={onQuery}
          searchRef={setSearchInputRef}
        />
      </div>

      <TabList
        idPrefix={CUSTOM_PLANS_TAB_ID_PREFIX}
        label={t('fleet.plans.custom.tabs_label')}
        items={items}
        selected={parsed().status}
        onSelect={setStatus}
      />

      <CustomPlansPanels
        items={items}
        status={parsed().status}
        query={plans}
        filters={{ repo: parsed().repo, q: parsed().q }}
        planParam={planParam}
        setPlanParam={setPlanParam}
        page={() => parsed().page}
        setPage={setPage}
        searchInputRef={getSearchInputRef}
        token={token}
      />
    </section>
  );
}
